<template>
  <div class="m-detail">
    <div v-if="loading" class="m-hint">{{ $t("mobile.loading") }}</div>
    <div v-else-if="!plan" class="m-hint">{{ $t("mobile.notFound") }}</div>

    <template v-else>
      <section class="m-block" :class="statusClass">
        <div class="m-head">
          <div>
            <h2 class="m-title">{{ plan.shipmentRecordName || "—" }}</h2>
            <span v-if="can('RecordStatus') && plan.recordStatus" class="m-status">{{ plan.recordStatus }}</span>
          </div>
          <span v-if="can('ChinaUser') && plan.chinaUser" class="m-avatar" :style="{ borderColor: plan.chinaStatusColor || '#bdbdbd' }">
            <user-avatar v-if="plan.chinaUserAvatar" :user-id="plan.chinaUser" />
            <i v-else class="material-icons">person</i>
          </span>
        </div>
      </section>

      <section v-if="showAmounts" class="m-figures">
        <div v-if="can('OrderTotalAmount')" class="m-figure">
          <p>{{ $t("shippingPlan.orderTotalAmount") }}</p>
          <strong>{{ plan.orderTotalAmount | formatAmount }}</strong>
        </div>
        <div v-if="can('PrepaymentAmount')" class="m-figure">
          <p>{{ $t("shippingPlan.prepaymentAmount") }}</p>
          <strong>{{ plan.prepaymentAmount | formatAmount }}</strong>
        </div>
      </section>

      <section v-for="group in groups" :key="group.key" class="m-block">
        <h3 class="m-heading">{{ group.label }}</h3>
        <dl class="m-rows">
          <template v-for="row in group.rows">
            <dt :key="row.key + '-t'">{{ row.label }}</dt>
            <dd :key="row.key + '-d'">
              <img v-if="row.logo" :src="row.logo" class="m-rowlogo" alt="" />
              <a v-if="row.href" :href="row.href" target="_blank" rel="noopener">{{ row.value }}</a>
              <template v-else>{{ row.value }}</template>
            </dd>
          </template>
        </dl>
      </section>

      <section v-if="products.length" class="m-block">
        <h3 class="m-heading">{{ $t("mobile.positions") }}</h3>
        <ul class="m-products">
          <li v-for="p in products" :key="p.linkedProductId">
            <span class="m-products__qty">{{ p.quantityDelivering }}&times;</span>
            <span class="m-products__sku">{{ p.sku }}</span>
            <span class="m-products__name">{{ p.name }}</span>
          </li>
        </ul>
      </section>

      <section v-if="documents.length" class="m-block">
        <h3 class="m-heading">{{ $t("shippingPlan.documents") }}</h3>
        <ul class="m-docs">
          <li v-for="d in documents" :key="d.id">
            <i class="material-icons">description</i>
            <span>{{ d.name || d.fileName }}</span>
          </li>
        </ul>
      </section>
    </template>
  </div>
</template>

<script>
import axiosFactory from "@/services/axios.factory";
import { UserAvatar } from "@/components";
import { hasPermission } from "@/mixins/auth";
import { loadMasterData, carrierLogo, placeLogo } from "@/helpers/shippingMasterData";

// Wie in der Liste und wie am Desktop (ShippingPlans.vue rowClassCallback).
const STATUS_CLASS = {
  "Delivery date in planning": "m-block--planning",
  "Delivery Date fixed": "m-block--fixed",
  Delivered: "m-block--delivered",
  Archived: "m-block--archived",
};

export default {
  name: "MobileShippingPlanDetail",
  components: { UserAvatar },
  data() {
    return {
      plan: null,
      products: [],
      documents: [],
      masterData: { carriers: [], places: [] },
      loading: true,
    };
  },
  computed: {
    showAmounts() {
      return this.can("OrderTotalAmount") || this.can("PrepaymentAmount");
    },
    statusClass() {
      return (this.plan && STATUS_CLASS[this.plan.recordStatus]) || "";
    },
    groups() {
      const p = this.plan;
      if (!p) return [];

      const datum = (v) => (v ? this.$options.filters.formatDate(v) : null);
      const zeit = (v) => (v ? this.$options.filters.formatDateTime(v) : null);
      const betrag = (v) => (v || v === 0 ? this.$options.filters.formatAmount(v) : null);
      const jaNein = (v) => (v === true ? this.$t("shippingPlan.yes") : v === false ? this.$t("shippingPlan.no") : null);
      // Statuswerte wie containerBookingStatus/mtcStatus sind Schluessel ("pending", "requested", …);
      // fehlt eine Uebersetzung, wird der Rohwert gezeigt statt eines leeren Feldes.
      const text = (key) => (key ? (this.$te(key) ? this.$t(key) : key) : null);

      const groups = [
        {
          key: "shipment",
          label: this.$t("shippingPlan.shipment"),
          rows: [
            { key: "tags", column: "RecordTags", label: this.$t("mobile.stickers"), value: p.recordTags },
            { key: "size", column: "ContainerSize", label: this.$t("shippingPlan.containerSize"), value: p.containerSize },
            { key: "carrier", column: "CarrierName", label: this.$t("shippingPlan.carrierName"), value: p.carrierName, logo: carrierLogo(this.masterData, p.carrierNameId) },
            { key: "booking", label: this.$t("shippingPlan.containerBookingStatus"), value: text(`shippingPlan.bookingStatus.${p.containerBookingStatus}`) },
            { key: "bookingDate", label: this.$t("shippingPlan.containerBookingDate"), value: datum(p.containerBookingDate) },
            { key: "container", column: "ContainerId", label: this.$t("shippingPlan.containerId"), value: p.containerId },
            { key: "carrierShipment", column: "CarrierShipmentId", label: this.$t("shippingPlan.carrierShipmentId"), value: p.carrierShipmentId },
            { key: "tracking", label: this.$t("shippingPlan.trackingUrl"), value: p.trackingUrl ? this.$t("mobile.openTracking") : null, href: p.trackingUrl },
            { key: "place", column: "ContainerArrivalPlace", label: this.$t("shippingPlan.containerArrivalPlace"), value: p.containerArrivalPlace, logo: placeLogo(this.masterData, p.containerArrivalPlaceId) },
            { key: "arrival", column: "ContainerArrivalDate", label: this.$t("shippingPlan.containerArrivalDate"), value: zeit(p.containerArrivalDate) },
            { key: "dataSent", label: this.$t("shippingPlan.productDataSent"), value: jaNein(p.productDataSent) },
            { key: "note", column: "ShipmentNote", label: this.$t("shippingPlan.shipmentNote"), value: p.shipmentNote },
          ],
        },
        {
          key: "order",
          label: this.$t("shippingPlan.order"),
          rows: [
            { key: "supplier", column: "SupplierName", label: this.$t("shippingPlan.supplierName"), value: p.supplierName },
            { key: "orderId", column: "SupplierOrderId", label: this.$t("shippingPlan.supplierOrderId"), value: p.supplierOrderId },
            { key: "payment", column: "PaymentStatus", label: this.$t("shippingPlan.paymentStatus"), value: p.paymentStatus },
            { key: "prepayDate", column: "PrepaymentDate", label: this.$t("shippingPlan.prepaymentDate"), value: datum(p.prepaymentDate) },
            { key: "fullpaid", column: "FullpaidDate", label: this.$t("shippingPlan.fullPaidDate"), value: datum(p.fullpaidDate) },
            { key: "freight", column: "ShipmentPaidStatus", label: this.$t("shippingPlan.shipmentPaidStatus"), value: p.shipmentPaidStatus },
            { key: "freightDoc", column: "ShipmentPaidStatus", label: this.$t("shippingPlan.shipmentDocumentNumber"), value: p.shipmentDocumentNumber },
            { key: "freightAmount", column: "ShipmentPaidStatus", label: this.$t("shippingPlan.shipmentAmount"), value: betrag(p.shipmentAmount) },
            { key: "customs", column: "CustomsPaidStatus", label: this.$t("shippingPlan.customsPaidStatus"), value: p.customsPaidStatus },
            { key: "customsDoc", column: "CustomsPaidStatus", label: this.$t("shippingPlan.customsDocumentNumber"), value: p.customsDocumentNumber },
            { key: "customsAmount", column: "CustomsPaidStatus", label: this.$t("shippingPlan.customsAmount"), value: betrag(p.customsAmount) },
          ],
        },
        {
          key: "china",
          label: this.$t("shippingPlan.china"),
          rows: [
            { key: "chinaStatus", column: "ChinaStatus", label: this.$t("shippingPlan.chinaStatus"), value: p.chinaStatus },
            { key: "chinaUser", column: "ChinaUser", label: this.$t("shippingPlan.chinaUser"), value: p.chinaUserFullName },
            { key: "chinaText", column: "ChinaText", label: this.$t("shippingPlan.chinaText"), value: p.chinaText },
            { key: "mtc", label: this.$t("shippingPlan.mtcStatus"), value: text(`shippingPlan.documentStatuses.${p.mtcStatus}`) },
            { key: "co", label: this.$t("shippingPlan.coStatus"), value: text(`shippingPlan.documentStatuses.${p.coStatus}`) },
          ],
        },
        {
          key: "accounting",
          label: this.$t("shippingPlan.accounting"),
          rows: [
            { key: "accStatus", column: "AccountingStatus", label: this.$t("shippingPlan.accountingStatus"), value: p.accountingStatus },
            { key: "accDate", column: "AccountingStatusDate", label: this.$t("shippingPlan.accountingStatusDate"), value: datum(p.accountingStatusDate) },
            { key: "wms", label: this.$t("shippingPlan.wmsBookedShort"), value: jaNein(p.wmsGebucht) },
            { key: "afterbuy", label: this.$t("shippingPlan.afterbuyBookedShort"), value: jaNein(p.afterbuyGebucht) },
          ],
        },
      ];

      // Felder ohne eigene Rechtespalte (Buchungsstatus, MTC/CO, Belegnummern) verhalten sich wie
      // am Desktop: sie haengen am Zugriff auf den Bereich, nicht an einer einzelnen Spalte.
      return groups
        .map((g) => ({ ...g, rows: g.rows.filter((r) => r.value && (!r.column || this.can(r.column))) }))
        .filter((g) => g.rows.length);
    },
  },
  created() {
    this.load();
  },
  methods: {
    can(column) {
      return hasPermission("LimitRead", "ShippingPlans", column);
    },
    async load() {
      this.masterData = await loadMasterData();
      try {
        const res = await axiosFactory().get(`/shipping_plans/${this.$route.params.id}`);
        this.plan = (res.data && res.data.data) || null;
        this.documents = (this.plan && this.plan.documents) || [];
        this.products = this.can("Products") && this.plan ? this.plan.products || [] : [];
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.m-detail {
  padding: 12px 12px 24px;
}

// Radius und Schatten wie .md-card im Desktop-Theme.
.m-block {
  background: #fff;
  border-radius: 6px;
  padding: 14px;
  margin-bottom: 10px;
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.14);

  // Statusfarben wie in der Desktop-Tabelle (ShippingPlans.css)
  &--planning {
    background: #cbc3e3;
  }

  &--fixed {
    background: lightgreen;
  }

  &--delivered {
    background: lightblue;
  }

  &--archived {
    background: #bdbdbd;
  }
}

.m-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

.m-avatar {
  flex: 0 0 auto;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 3px solid #bdbdbd;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  i {
    font-size: 22px;
    color: #bdbdbd;
  }
}

.m-title {
  font-size: 17px;
  font-weight: 500;
  margin: 0;
  line-height: 1.3;
}

.m-status {
  display: inline-block;
  margin-top: 8px;
  font-size: 11px;
  background: rgba(255, 255, 255, 0.75);
  border-radius: 3px;
  padding: 2px 9px;
  color: #424242;
}

.m-rowlogo {
  height: 16px;
  width: 16px;
  object-fit: contain;
  vertical-align: middle;
  margin-right: 5px;
}

.m-figures {
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
}

.m-figure {
  flex: 1;
  background: #fff;
  border-radius: 12px;
  padding: 12px 14px;

  p {
    font-size: 12px;
    color: #757575;
    margin: 0;
  }

  strong {
    font-size: 17px;
    font-weight: 500;
  }
}

.m-rows {
  margin: 0;
  display: grid;
  grid-template-columns: minmax(0, 47%) minmax(0, 53%);
  gap: 0;

  dt,
  dd {
    padding: 8px 0;
    border-bottom: 1px solid #f0f0f0;
    font-size: 13px;
    margin: 0;
    min-width: 0;
  }

  dt {
    color: #757575;
    padding-right: 10px;
    // Beschriftungen nur an Wortgrenzen umbrechen — sonst steht dort „Containerbuchungsst/atus".
    overflow-wrap: normal;
    word-break: normal;
    hyphens: auto;
  }

  dd {
    text-align: right;
    // Werte duerfen hart umbrechen (lange Containernummern, Belegnummern).
    overflow-wrap: anywhere;

    a {
      color: #4caf50;

      &:hover,
      &:focus {
        color: #4caf50;
      }
    }
  }

  dt:last-of-type,
  dd:last-of-type {
    border-bottom: 0;
  }
}

.m-heading {
  font-size: 13px;
  color: #757575;
  margin: 0 0 8px;
  font-weight: 400;
}

.m-products {
  list-style: none;
  margin: 0;
  padding: 0;

  li {
    display: flex;
    align-items: baseline;
    gap: 8px;
    padding: 7px 0;
    border-bottom: 1px solid #f0f0f0;
    font-size: 13px;

    &:last-child {
      border-bottom: 0;
    }
  }

  &__qty {
    flex: 0 0 auto;
    font-weight: 500;
  }

  &__sku {
    flex: 0 0 auto;
  }

  &__name {
    color: #9e9e9e;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.m-docs {
  list-style: none;
  margin: 0;
  padding: 0;

  li {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 7px 0;
    font-size: 13px;

    i {
      font-size: 18px;
      color: #9e9e9e;
    }
  }
}

.m-hint {
  text-align: center;
  color: #9e9e9e;
  font-size: 14px;
  padding: 32px 0;
}
</style>
